import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/lib/i18n';
import { getCookieConsent, setCookieConsent } from '@/lib/cookieConsent';
import { prefetchRoute } from '@/lib/routePrefetch';

const CookieConsentBanner = () => {
  const { lang } = useLanguage();
  const existing = typeof window !== 'undefined' ? getCookieConsent() : null;
  const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  const [visible, setVisible] = useState(!existing);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (!visible || isAdminRoute) return null;

  const text = {
    de: {
      title: 'Cookie-Einstellungen',
      desc: 'Wir verwenden notwendige Cookies für den Betrieb der Website. Optionale Cookies für Analytics und Marketing nutzen wir nur mit Ihrer Einwilligung.',
      required: 'Notwendige Cookies (immer aktiv)',
      analytics: 'Analytics-Cookies',
      marketing: 'Marketing-Cookies',
      more: 'Mehr in der Datenschutzerklärung',
      acceptAll: 'Alle akzeptieren',
      necessaryOnly: 'Nur notwendige',
      settings: 'Einstellungen',
      save: 'Auswahl speichern',
    },
    bs: {
      title: 'Postavke kolačića',
      desc: 'Koristimo nužne kolačiće za rad web stranice. Opcionalne kolačiće za analitiku i marketing koristimo samo uz vaš pristanak.',
      required: 'Nužni kolačići (uvijek aktivni)',
      analytics: 'Analitički kolačići',
      marketing: 'Marketinški kolačići',
      more: 'Više u izjavi o zaštiti podataka',
      acceptAll: 'Prihvati sve',
      necessaryOnly: 'Samo nužni',
      settings: 'Postavke',
      save: 'Sačuvaj izbor',
    },
    en: {
      title: 'Cookie settings',
      desc: 'We use necessary cookies to operate the website. Optional analytics and marketing cookies are used only with your consent.',
      required: 'Necessary cookies (always active)',
      analytics: 'Analytics cookies',
      marketing: 'Marketing cookies',
      more: 'More in the privacy policy',
      acceptAll: 'Accept all',
      necessaryOnly: 'Necessary only',
      settings: 'Settings',
      save: 'Save selection',
    },
  }[lang];

  const closeWith = (next: { analytics: boolean; marketing: boolean }) => {
    setCookieConsent(next);
    setVisible(false);
  };

  const prefetchPrivacy = () => {
    prefetchRoute('/datenschutz');
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] border-t border-border bg-card/95 backdrop-blur-md">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-3xl space-y-2">
            <p className="font-semibold text-foreground">{text.title}</p>
            <p className="text-sm text-foreground/80">
              {text.desc}{' '}
              <a
                href="/datenschutz"
                className="text-primary hover:underline"
                onMouseEnter={prefetchPrivacy}
                onFocus={prefetchPrivacy}
                onTouchStart={prefetchPrivacy}
              >
                {text.more}
              </a>
            </p>

            {showSettings && (
              <div className="mt-2 grid gap-2 rounded-lg border border-border bg-background/60 p-3">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-sm text-foreground/80">{text.required}</Label>
                  <Switch checked disabled />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="cookie-analytics" className="text-sm text-foreground/80">
                    {text.analytics}
                  </Label>
                  <Switch id="cookie-analytics" checked={analytics} onCheckedChange={setAnalytics} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="cookie-marketing" className="text-sm text-foreground/80">
                    {text.marketing}
                  </Label>
                  <Switch id="cookie-marketing" checked={marketing} onCheckedChange={setMarketing} />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            {!showSettings && (
              <Button variant="outline" onClick={() => setShowSettings(true)}>
                {text.settings}
              </Button>
            )}
            {showSettings && (
              <Button variant="outline" onClick={() => closeWith({ analytics, marketing })}>
                {text.save}
              </Button>
            )}
            <Button variant="secondary" onClick={() => closeWith({ analytics: false, marketing: false })}>
              {text.necessaryOnly}
            </Button>
            <Button onClick={() => closeWith({ analytics: true, marketing: true })}>{text.acceptAll}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
